import {
  createContext,
  useContext,
  useId,
  useState,
  type ComponentPropsWithoutRef,
  type JSX,
  type ReactNode,
} from 'react';

import { cx } from './internal/cx.js';

interface AccordionContextValue {
  expanded: string | false;
  setExpanded: (value: string | false) => void;
}

const AccordionContext = createContext<AccordionContextValue | null>(null);

interface AccordionItemContextValue {
  itemId: string;
  isExpanded: boolean;
  toggle: () => void;
  contentId: string;
  triggerId: string;
}

const AccordionItemContext = createContext<AccordionItemContextValue | null>(null);

function useAccordionContext(): AccordionContextValue {
  const ctx = useContext(AccordionContext);
  if (!ctx) throw new Error('Accordion components must be used within Accordion.Root');
  return ctx;
}

function useAccordionItemContext(): AccordionItemContextValue {
  const ctx = useContext(AccordionItemContext);
  if (!ctx) throw new Error('Accordion.Item components must be used within Accordion.Item');
  return ctx;
}

export interface AccordionRootProps extends ComponentPropsWithoutRef<'div'> {
  children: ReactNode;
  /** Controlled expanded item id(s). Pass `false` when none expanded. */
  expanded?: string | false;
  /** Called when expanded state changes. */
  onExpandedChange?: (value: string | false) => void;
  /** Default expanded item when uncontrolled. */
  defaultExpanded?: string | false;
}

function AccordionRoot({
  children,
  className,
  expanded: expandedProp,
  onExpandedChange,
  defaultExpanded = false,
  ...props
}: AccordionRootProps): JSX.Element {
  const [internalExpanded, setInternalExpanded] = useState<string | false>(defaultExpanded);
  const isControlled = expandedProp !== undefined;
  const expanded = isControlled ? expandedProp : internalExpanded;

  function setExpanded(value: string | false): void {
    if (!isControlled) setInternalExpanded(value);
    onExpandedChange?.(value);
  }

  return (
    <AccordionContext.Provider value={{ expanded, setExpanded }}>
      <div className={cx('nrx-accordion', className)} {...props}>
        {children}
      </div>
    </AccordionContext.Provider>
  );
}

export interface AccordionItemProps extends ComponentPropsWithoutRef<'div'> {
  children: ReactNode;
  /** Unique id for this panel within the accordion. */
  value: string;
}

function AccordionItem({ children, className, value, ...props }: AccordionItemProps): JSX.Element {
  const { expanded, setExpanded } = useAccordionContext();
  const baseId = useId();
  const triggerId = `${baseId}-trigger`;
  const contentId = `${baseId}-content`;
  const isExpanded = expanded === value;

  function toggle(): void {
    if (isExpanded) {
      setExpanded(false);
      return;
    }
    setExpanded(value);
  }

  return (
    <AccordionItemContext.Provider
      value={{ itemId: value, isExpanded, toggle, contentId, triggerId }}
    >
      <div
        className={cx('nrx-accordion__item', className)}
        data-expanded={isExpanded || undefined}
        {...props}
      >
        {children}
      </div>
    </AccordionItemContext.Provider>
  );
}

export interface AccordionSummaryProps extends ComponentPropsWithoutRef<'button'> {
  children: ReactNode;
}

function AccordionSummary({
  children,
  className,
  onClick,
  ...props
}: AccordionSummaryProps): JSX.Element {
  const { isExpanded, toggle, contentId, triggerId } = useAccordionItemContext();

  return (
    <button
      type="button"
      id={triggerId}
      aria-expanded={isExpanded}
      aria-controls={contentId}
      className={cx('nrx-accordion__summary', className)}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) toggle();
      }}
      {...props}
    >
      {children}
      <span className="nrx-accordion__chevron" aria-hidden="true" />
    </button>
  );
}

export interface AccordionContentProps extends ComponentPropsWithoutRef<'div'> {
  children: ReactNode;
}

function AccordionContent({ children, className, ...props }: AccordionContentProps): JSX.Element {
  const { isExpanded, contentId, triggerId } = useAccordionItemContext();

  return (
    <div
      id={contentId}
      role="region"
      aria-labelledby={triggerId}
      aria-hidden={!isExpanded}
      className={cx(
        'nrx-accordion__content',
        isExpanded && 'nrx-accordion__content--open',
        className,
      )}
      {...(!isExpanded ? { inert: true } : {})}
      {...props}
    >
      <div className="nrx-accordion__content-inner">
        <div className="nrx-accordion__content-body">{children}</div>
      </div>
    </div>
  );
}

/** Expandable panel primitive for stacked sections. Requires `@import '@nrx/ui/theme.css'`. */
export const Accordion = {
  Root: AccordionRoot,
  Item: AccordionItem,
  Summary: AccordionSummary,
  Content: AccordionContent,
};
